<template>
  <div
    v-if="error"
    class="my-[16px] p-[14px] rounded-[10px] border-[1px] border-[#E2E2E2] bg-[#FCFCFC] h-[300px] flex items-center justify-center"
  >
    <div class="text-center flex items-center justify-center flex-wrap gap-[16px]">
      <p class="mb-0">
        Mohon maaf, detail campaign tidak dapat ditampilkan saat ini
      </p>
      <div>
        <b-button
          variant="primary"
          size="sm"
          @click="$emit('retry')"
        >Coba Lagi</b-button>
      </div>
    </div>
  </div>
  <div
    v-else
    class="my-[16px] p-[14px] rounded-[10px] border-[1px] border-[#E2E2E2] bg-[#FCFCFC]"
  >
    <div
      class="bg-[#F4F4F4] min-h-[210px] rounded-[10px]"
      :class="{ 'animate-pulse': loading }"
    >
      <img
        v-if="!isLoading && campaign.thumbnail_url"
        :src="campaign.thumbnail_url"
        loading="lazy"
        class="w-full h-full min-h-[210px] object-cover rounded-[10px]"
        alt="Campaign Cover Image"
      >
    </div>
    <div class="mt-[14px] pb-[20px] border-b-[1px] border-[#E2E2E2] border-dashed flex items-start justify-between gap-[120x] flex-wrap">
      <div class="flex items-center justify-start gap-[12px] flex-wrap grow">
        <h3
          ref="campaignName"
          v-b-popover.hover="{
            content: !isLoading ? (campaign.name || '-') : '',
            customClass: 'scala-popover',
            disabled: !isNameTruncated,
          }"
          :class="{ 'w-full max-w-[200px] rounded-[10px] animate-pulse bg-[#F4F4F4] h-[20px]': isLoading}"
          class="text-[14px] text-[#000000] !font-semibold mb-0 line-clamp-2 max-w-[300px]"
        >
          {{ !isLoading ? (campaign.name || '-') : '' }}
        </h3>
        <b-button
          v-if="showEndButton"
          variant="outline-primary"
          size="sm"
          class="!flex items-center justify-center gap-[10px] !text-[14px] !font-semibold !px-[10px] !py-[4px]"
          :disabled="loading || mutating || disableEnded || isEnded || disabled"
          @click="handleEndClick"
        ><span
          v-if="!mutating"
          class="k-minus-cirlce text-[20px]"
        /><b-spinner
          v-else
          variant="primary"
          small
          class="!-mt-[4px]"
        />Akhiri Campaign</b-button>
        <b-button
          v-if="showCancelButton"
          variant="outline-danger"
          size="sm"
          class="!flex items-center justify-center gap-[10px] !text-[14px] !font-semibold !px-[10px] !py-[4px]"
          :disabled="loading || mutatingCancel || isCancelled || disabled"
          @click="handleCancelClick"
        ><b-spinner
          v-if="mutatingCancel"
          variant="danger"
          small
          class="!-mt-[4px]"
        />Batalkan Campaign</b-button>
      </div>
      <div class="flex flex-wrap items-center justify-end gap-[12px]">
        <!-- <b-button
          variant="outline-primary"
          size="sm"
          :disabled="loading"
        >Broadcast Template</b-button> -->
        <b-button
          v-if="showEditButton"
          variant="outline-primary"
          size="sm"
          class="!flex items-center justify-center gap-[10px]"
          :disabled="loading || isCancelled || disabled"
          @click="handleEditClick"
        ><span class="k-edit" />Edit</b-button>
      </div>
    </div>
    <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-[16px] px-[24px] mt-[16px]">
      <info-item
        title="Campaign Tipe"
        icon="k-flag"
        :loading="isLoading"
      >
        <span
          ref="campaignType"
          v-b-popover.hover="{
            content: campaignType,
            customClass: 'scala-popover',
            disabled: !isTypeTruncated,
          }"
          class="line-clamp-2 cursor-default"
        >{{ campaignType }}</span>
      </info-item>
      <info-item
        title="Campaign Kategori"
        icon="k-sort"
        :loading="isLoading"
      >
        <span
          ref="campaignCategory"
          v-b-popover.hover="{
            content: campaignCategory,
            customClass: 'scala-popover',
            disabled: !isCategoryTruncated,
          }"
          class="line-clamp-2 cursor-default"
        >{{ campaignCategory }}</span>
      </info-item>
      <info-item
        title="Total Creator"
        icon="k-people"
        :loading="isLoading"
      >
        <span class="text-[#F95031]">{{ campaign.registered_creators }}</span>/{{ campaign.total_creators }}
      </info-item>
      <info-item
        title="Duration"
        icon="k-calendar"
        :loading="isLoading"
      >
        {{ parseDate(campaign.start_date) }} - {{ parseDate(campaign.end_date) }}
      </info-item>
    </div>
  </div>
</template>

<script>
import { DDMMMMYYYY } from '@/libs/filterDate'
import InfoItem from './InfoItem.vue'

export default {
  components: {
    InfoItem,
  },
  props: {
    tab: {
      type: String,
      default: 'pre campaign',
    },
    campaign: {
      type: Object,
      default: () => ({
        campaign_type: [],
        product_category: [],
        start_date: '',
        end_date: '',
        total_creators: 0,
      }),
    },
    loading: {
      type: Boolean,
      default: true,
    },
    error: {
      type: String,
      default: undefined,
    },
    mutating: {
      type: Boolean,
      default: false,
    },
    disableEnded: {
      type: Boolean,
      default: false,
    },
    mutatingCancel: {
      type: Boolean,
      default: false,
    },
    status: {
      type: String,
      default: '',
    },
    phase: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isNameTruncated: false,
      isTypeTruncated: false,
      isCategoryTruncated: false,
      data: {
        campaign_type: [],
        product_category: [],
        total_creators: 0,
        start_date: '',
        end_date: '',
        name: '',
        thumbnail_url: '',
      },
    }
  },
  computed: {
    normalizedStatus() {
      const s = this.campaign?.status || this.status || this.$route.query.tab || this.$route.query.status || ''
      return String(s).trim().replace(/\s/g, '_').toLowerCase()
    },
    isEnded() {
      return this.normalizedStatus === 'ended'
    },
    isCancelled() {
      return ['cancel', 'cancelled', 'canceled'].includes(this.normalizedStatus)
    },
    isDraft() {
      return this.normalizedStatus === 'draft'
    },
    effectivePhase() {
      if (this.phase) return this.phase
      if (this.isEnded) return 'ongoing'
      if (this.isCancelled || this.isDraft) return 'pre_campaign'
      const currentTab = (this.tab || this.$route.query.tab || 'pre campaign').toLowerCase()
      return currentTab.includes('ongoing') ? 'ongoing' : 'pre_campaign'
    },
    showEndButton() {
      return (this.isEnded || this.status === 'ongoing') && this.effectivePhase === 'ongoing'
    },
    showCancelButton() {
      return (this.isCancelled || this.status === 'pre_campaign') && this.effectivePhase === 'pre_campaign'
    },
    showEditButton() {
      return (this.isCancelled || ['pre_campaign', 'draft'].includes(this.status)) && this.effectivePhase === 'pre_campaign'
    },
    campaignType() {
      if (!this.campaign || !this.campaign.campaign_type) return '-'
      return this.campaign.campaign_type.join(', ')
    },
    campaignCategory() {
      if (!this.campaign || !this.campaign.product_category) return '-'
      return this.campaign.product_category.join(', ')
    },
    isLoading() {
      return this.loading && !this.campaign.name
    },
  },
  watch: {
    'campaign.name': {
      handler() {
        this.$nextTick(() => setTimeout(this.checkTruncation, 50))
      },
    },
    'campaign.product_category': {
      handler() {
        this.$nextTick(() => setTimeout(this.checkTruncation, 50))
      },
    },
    'campaign.campaign_type': {
      handler() {
        this.$nextTick(() => setTimeout(this.checkTruncation, 50))
      },
    },
    isLoading: {
      handler(val) {
        if (!val) this.$nextTick(() => setTimeout(this.checkTruncation, 100))
      },
    },
  },
  mounted() {
    this.$nextTick(() => setTimeout(this.checkTruncation, 50))
  },
  beforeDestroy() {},
  methods: {
    parseDate(date) {
      if (!date) return '-'
      return DDMMMMYYYY(date)
    },
    handleEditClick() {
      if (this.isCancelled || this.disabled || this.loading) return
      this.$router.push({ name: 'edit-campaign-komlabs', params: { id: this.$route.params.id } })
    },
    handleCancelClick() {
      if (this.isCancelled || this.disabled || this.loading || this.mutatingCancel) return
      this.$emit('campaign-cancelled')
    },
    handleEndClick() {
      if (this.isEnded || this.disabled || this.loading || this.mutating || this.disableEnded) return
      this.$emit('campaign-ended')
    },
    checkTruncation() {
      const MAX_LENGTH = 100

      const nameEl = this.$refs.campaignName
      if (nameEl) {
        const text = nameEl.textContent || ''
        this.isNameTruncated = text.trim().length >= MAX_LENGTH
      }

      const typeEl = this.$refs.campaignType
      if (typeEl) {
        const text = typeEl.textContent || ''
        this.isTypeTruncated = text.trim().length >= MAX_LENGTH
      }

      const categoryEl = this.$refs.campaignCategory
      if (categoryEl) {
        const text = categoryEl.textContent || ''
        this.isCategoryTruncated = text.trim().length >= MAX_LENGTH
      }
    },
  },
}
</script>
